import { request, uploadImage as uploadImageRequest } from './client';
import type {
  DashboardOverview,
  LoginResponse,
  Paginated,
  PostPayload,
  PostVO,
  Profile,
  Project,
  ProjectPayload,
} from './types';

/* ---------------- 公开接口 ---------------- */

export const fetchProfile = () => request<Profile>('/api/profile');

export interface PostListParams {
  page?: number;
  pageSize?: number;
  tag?: string;
  keyword?: string;
}

export const fetchPosts = (params: PostListParams = {}) => {
  const search = new URLSearchParams();
  if (params.page) search.set('page', String(params.page));
  if (params.pageSize) search.set('pageSize', String(params.pageSize));
  if (params.tag) search.set('tag', params.tag);
  if (params.keyword) search.set('keyword', params.keyword);
  const query = search.toString();
  return request<Paginated<PostVO>>(`/api/posts${query ? `?${query}` : ''}`);
};

export const fetchPostBySlug = (slug: string) =>
  request<PostVO>(`/api/posts/${encodeURIComponent(slug)}`);

export const fetchProjects = (tag?: string) =>
  request<Project[]>(`/api/projects${tag ? `?tag=${encodeURIComponent(tag)}` : ''}`);

/* ---------------- 认证 ---------------- */

export const login = (username: string, password: string) =>
  request<LoginResponse>('/api/auth/login', {
    method: 'POST',
    body: { username, password },
    auth: false,
  });

/* ---------------- 管理端：仪表盘 ---------------- */

export const fetchDashboard = () => request<DashboardOverview>('/api/admin/dashboard');

/* ---------------- 管理端：文章 ---------------- */

export interface AdminPostListParams {
  page?: number;
  pageSize?: number;
  status?: 'draft' | 'published';
  tag?: string;
  keyword?: string;
}

export const fetchAdminPosts = (params: AdminPostListParams = {}) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') search.set(key, String(value));
  });
  const query = search.toString();
  return request<Paginated<PostVO>>(`/api/admin/posts${query ? `?${query}` : ''}`);
};

export const fetchAdminPost = (id: number) =>
  request<PostVO>(`/api/admin/posts/${id}`);

export const createPost = (payload: PostPayload) =>
  request<PostVO>('/api/admin/posts', { method: 'POST', body: payload });

export const updatePost = (id: number, payload: Partial<PostPayload>) =>
  request<PostVO>(`/api/admin/posts/${id}`, { method: 'PATCH', body: payload });

export const deletePost = (id: number) =>
  request<{ success: boolean }>(`/api/admin/posts/${id}`, { method: 'DELETE' });

/* ---------------- 管理端：项目 ---------------- */

export const fetchAdminProjects = () => request<Project[]>('/api/admin/projects');

export const createProject = (payload: ProjectPayload) =>
  request<Project>('/api/admin/projects', { method: 'POST', body: payload });

export const updateProject = (id: number, payload: Partial<ProjectPayload>) =>
  request<Project>(`/api/admin/projects/${id}`, { method: 'PATCH', body: payload });

export const deleteProject = (id: number) =>
  request<{ success: boolean }>(`/api/admin/projects/${id}`, { method: 'DELETE' });

/* ---------------- 管理端：个人资料 ---------------- */

export const fetchAdminProfile = () => request<Profile>('/api/admin/profile');

export const updateProfile = (payload: Omit<Profile, 'id' | 'updatedAt'>) =>
  request<Profile>('/api/admin/profile', { method: 'PUT', body: payload });

/* ---------------- 文件上传 ---------------- */

export const uploadImage = (file: File) => uploadImageRequest(file);

/** 音频等大文件上传（后台统一入口，图片仍限制 2MB） */
export const uploadFile = (file: File) => uploadImageRequest(file);
